import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template174: Alcatraz - bloques separados (hero, intro, ferry, alcatrazBox, laVisita, bottomBox)
const Template174 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const isSmallScreen = useWindowSize().width <= 600;
	const contentColor = textColor || "#444";

	const getImageSrc = (id) =>
		page.images?.find((img) => img.id === id)?.src ?? "";
	const getCanvasImageUrl = (id) =>
		page.canvasImages?.find((img) => img.id === id)?.imageUrl ?? "";

	const headerText = page.texts?.[0]?.content ?? "";
	const introText = page.texts?.[1]?.content ?? "";
	const logoImage = getCanvasImageUrl("logo");
	const hero = page?.hero ?? page?.data?.hero ?? page?.data?.data?.hero ?? {};
	const heroImageSrc = getImageSrc(hero.imageId);
	const heroStickerSrc = getImageSrc(hero.stickerImageId);
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const ferry = sections[0] ?? {};
	const alcatrazBox = sections[1] ?? {};
	const laVisita = sections[2] ?? {};
	const bottomBox =
		page?.bottomBox ??
		page?.data?.bottomBox ??
		page?.data?.data?.bottomBox ??
		{};

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};
	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#F2B705",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: "'Poppins', sans-serif",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{heroImageSrc && (
				<Box data-block-id="image-0" sx={{ position: "relative" }}>
					<Box
						component="img"
						src={heroImageSrc}
						alt="Alcatraz"
						sx={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
					{hero.title && (
						<Box
							component="h1"
							sx={{
								fontFamily: "'Poppins', sans-serif",
								fontSize: isSmallScreen ? 28 : 32,
								textAlign: "center",
								position: "absolute",
								bottom: 0,
								margin: "10px",
								left: 20,
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							}}
						>
							{hero.title}
						</Box>
					)}
					{heroStickerSrc && (
						<Box
							component="img"
							src={heroStickerSrc}
							alt="Alcatraz"
							sx={{ width: 100, position: "absolute", bottom: 60, left: 10 }}
						/>
					)}
				</Box>
			)}

			{introText && (
				<Box
					data-block-id="text-1"
					sx={{
						padding: "10px 12px",
						paddingBottom: "5px",
						position: "relative",
					}}
				>
					<Box
						component="p"
						sx={{
							fontFamily: "'Poppins', sans-serif",
							fontSize: 13,
							color: "#333",
							margin: 0,
							padding: 0,
						}}
					>
						{introText}
					</Box>
				</Box>
			)}

			<Box
				sx={{
					padding: "10px 12px",
					paddingBottom: 6,
					position: "relative",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 1.5,
						marginTop: 0,
					}}
				>
					{/* ——— Ferry a Alcatraz ——— */}
					<Box data-block-id="text-2" sx={{ display: "flex", gap: 2 }}>
						{getImageSrc(ferry.imageId) && (
							<Box
								component="img"
								src={getImageSrc(ferry.imageId)}
								alt=""
								sx={{
									width: "40%",
									height: 110,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						)}
						<Box sx={{ width: getImageSrc(ferry.imageId) ? "60%" : "100%" }}>
							{ferry.title && (
								<Box component="h3" sx={{ ...estiloH3, marginBottom: "0px" }}>
									{ferry.title}
								</Box>
							)}
							{ferry.body && (
								<Box
									component="p"
									sx={{
										fontSize: 13,
										color: contentColor,
										fontFamily: "'Poppins', sans-serif",
										marginTop: "0px",
									}}
								>
									{ferry.body}
								</Box>
							)}
						</Box>
					</Box>

					{/* ——— Alcatraz (caja con tooltip + imagen) ——— */}
					{(alcatrazBox.title || alcatrazBox.body) && (
						<Box
							data-block-id="text-3"
							sx={{
								display: "flex",
								gap: 2,
								position: "relative",
								marginTop: isSmallScreen ? 2 : 0,
							}}
						>
							<Box sx={{ width: "100%" }}>
								<Box
									component="p"
									sx={{
										...estiloP,
										padding: "20px",
										backgroundColor: "#fbf0cf",
										borderRadius: "12px",
										position: "relative",
									}}
								>
									{alcatrazBox.tooltipText && (
										<Box
											sx={{
												position: "absolute",
												bottom: isSmallScreen ? 120 : 100,
												right: 4,
												backgroundColor: "#fff8db",
												border: "2px solid #f5c400",
												borderRadius: "16px 16px 0px 16px",
												padding: "10px 12px",
												fontSize: 11,
												fontFamily: "'Poppins', sans-serif",
												color: contentColor,
												boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
												maxWidth: 200,
												zIndex: 6,
											}}
											dangerouslySetInnerHTML={{
												__html: alcatrazBox.tooltipText,
											}}
										/>
									)}
									{getImageSrc(alcatrazBox.tooltipImageId) && (
										<Box
											component="img"
											src={getImageSrc(alcatrazBox.tooltipImageId)}
											alt=""
											sx={{
												width: isSmallScreen ? 105 : 115,
												float: "right",
												marginRight: "-34px",
												marginTop: "-25px",
											}}
										/>
									)}
									{alcatrazBox.title && (
										<Box component="h3" sx={estiloH3}>
											{alcatrazBox.title}
										</Box>
									)}
									{alcatrazBox.body}
								</Box>
							</Box>
						</Box>
					)}

					{/* ——— La Visita a la Prisión ——— */}
					<Box data-block-id="text-4" sx={{ display: "flex", gap: 2 }}>
						<Box sx={{ width: "100%" }}>
							{laVisita.title && (
								<Box component="h3" sx={estiloH3}>
									{laVisita.title}
								</Box>
							)}
							{laVisita.body && (
								<Box component="p" sx={estiloP}>
									{laVisita.body}
								</Box>
							)}
						</Box>
					</Box>

					{/* ——— Entrada + Sabías que + enlace (bottomBox) ——— */}
					{bottomBox.sabiasQue?.length > 0 && (
						<Box
							data-block-id="text-5"
							sx={{
								backgroundColor: "#fbf0cf",
								border: "2px solid #F2B705",
								borderRadius: "12px",
								padding: "8px 16px",
								fontSize: "13px",
								color: contentColor,
								marginTop: -0.5,
								textAlign: "center",
								marginBottom: isSmallScreen ? 2 : 0,
								position: "relative",
							}}
						>
							{bottomBox.sabiasQue.map((sq, i) => (
								<Box
									key={i}
									sx={{
										position: "absolute",
										top: isSmallScreen ? 80 : 50,
										left: `${30 + i * 12}%`,
									}}
								>
									<SabiasQue icono={sq.icono} texto={sq.texto} />
								</Box>
							))}
							{bottomBox.entryText && (
								<Box component="p" sx={estiloP}>
									{bottomBox.entryText}
								</Box>
							)}
							{bottomBox.linkUrl && bottomBox.linkLabel && (
								<Box component="p" sx={estiloP}>
									<Box
										component="a"
										href={bottomBox.linkUrl}
										target="_blank"
										rel="noopener noreferrer"
										sx={{
											color: "#ca9904",
											fontWeight: 700,
											textDecoration: "underline",
										}}
									>
										{bottomBox.linkLabel}
									</Box>
								</Box>
							)}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template174.thumbnail = "/images/template-1.png";
export default Template174;
